import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCandidateProfile,
  fetchCandidateProfileScore,
  updateCandidateProfileSection,
  fetchCandidateOnboardingStatus,
  type CandidateProfile,
  type CandidateProfileScore,
  type CandidateProfileSectionEntry,
  type CandidateOnboardingStatusResponse,
} from '../api/candidate';
import type { ProfileSectionSlug } from '../types/profile';
import { deriveCandidateTeamSnapshot, type CandidateTeamSnapshot } from '../lib/candidateTeam';

export function useCandidateProfileQuery() {
  return useQuery({
    queryKey: ['candidate-profile'],
    queryFn: fetchCandidateProfile,
    staleTime: 1000 * 60,
  });
}

export function useCandidateProfileSection(slug: ProfileSectionSlug, enabled = true) {
  return useQuery({
    queryKey: ['candidate-profile', slug],
    queryFn: async () => {
      const profile = await fetchCandidateProfile();
      return profile[slug] ?? [];
    },
    enabled,
    staleTime: 1000 * 60,
  });
}

export function useUpdateCandidateProfileSection(slug: ProfileSectionSlug) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entries: CandidateProfileSectionEntry[]) => updateCandidateProfileSection(slug, entries),
    onSuccess: (data) => {
      queryClient.setQueryData<CandidateProfile>(['candidate-profile'], (prev) => ({
        ...(prev ?? {}),
        [slug]: data.entries,
      }));
      queryClient.setQueryData(['candidate-profile', slug], data.entries);
    },
  });
}

export function useCandidateProfileScore() {
  return useQuery<CandidateProfileScore>({
    queryKey: ['candidate-profile-score'],
    queryFn: fetchCandidateProfileScore,
    staleTime: 1000 * 60,
  });
}

export function useCandidateOnboardingStatus(enabled = true) {
  return useQuery<CandidateOnboardingStatusResponse>({
    queryKey: ['candidate-onboarding-status'],
    queryFn: fetchCandidateOnboardingStatus,
    enabled,
    staleTime: 1000 * 60,
  });
}

export function useCandidateTeamSnapshot(enabled = true) {
  return useQuery<CandidateProfile, Error, CandidateTeamSnapshot>({
    queryKey: ['candidate-profile'],
    queryFn: fetchCandidateProfile,
    staleTime: 1000 * 60,
    enabled,
    select: deriveCandidateTeamSnapshot,
  });
}
